import {
  DEFAULT_AXIS_LABEL_FONT_SIZE,
  DEFAULT_AXIS_SPLIT_NUMBER,
  DEFAULT_TITLE_FONT_SIZE
} from "constants/chart.constants";
import { THEME_FONT_FAMILY } from "constants/style.constants";
import {
  formatChartTitleWithSubscript,
  toUnicodeSubscript
} from "entities/charts/eva/translators/utils";

import { IChartResult } from "models/model";

import {
  IAxisMinMax,
  LegendT
} from "components/multiphase-chart/context/ChartSettingsContext";
import {
  IChartPreset,
  IMultiphaseChartSettings
} from "components/multiphase-chart/models/shared.models";

import { AXIS_NAME_TEXT_STYLE } from "../../constants/shared.constants";
import { shared } from "./";

export const getRateDateChartOptions = (
  option: IChartPreset,
  data: IChartResult,
  settings: IMultiphaseChartSettings,
  axisMinMax: IAxisMinMax,
  legend: LegendT
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  const series = shared.getDefaultLineSeries(option, data);
  const legendData = shared.getLegendData(series);

  let shownSplitLineY = false;

  return {
    grid: {
      right: 100,
      left: 100,
      top: 50,
      bottom: 100
    },
    title: {
      text: data.title,
      textStyle: {
        fontSize: DEFAULT_TITLE_FONT_SIZE,
        fontWeight: "bold",
        color: "#041C2C"
      },
      padding: [14, 0, 10, 16]
    },
    textStyle: { fontFamily: THEME_FONT_FAMILY, fontSize: 12, color: "#041C2C" },
    series: series,
    xAxis: data.layout.xAxis2.map((axis, i) => {
      const showMinorSplitLineX = settings.logs && settings.logs[i].x;
      return {
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: { color: "rgb(155,155,155)", width: 1 }
        },
        axisTick: {
          show: true,
          lineStyle: {
            width: 1
          }
        },
        id: i,
        type: "time",
        name: formatChartTitleWithSubscript(axis.title),
        min: axisMinMax[i].xMin !== null ? String(axisMinMax[i].xMin) : null,
        max: axisMinMax[i].xMax !== null ? String(axisMinMax[i].xMax) : null,
        nameLocation: "middle",
        nameGap: 30,
        nameTextStyle: AXIS_NAME_TEXT_STYLE,
        axisLabel: { fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE },
        offset: 0,
        alignTicks: shared.alignTicks,
        position: "bottom",
        splitLine: {
          show: true
        },
        minorSplitLine: {
          show: showMinorSplitLineX,
          lineStyle: { color: "#F2F2F2" }
        }
      };
    }),
    yAxis: data.layout.yAxis2.map((axis, i) => {
      const position = axis.isSecondaryYAxis ? "right" : "left";
      let showSplitLine = false;
      const showMinorSplitLineY = settings.logs && settings.logs[i].y;
      if (!shownSplitLineY && position === "left") {
        shownSplitLineY = true;
        showSplitLine = true;
      }
      return {
        axisLine: {
          show: true,
          onZero: false,
          lineStyle: { color: "rgb(155,155,155)", width: 1 }
        },
        axisTick: {
          show: true,
          lineStyle: {
            width: 1
          }
        },
        id: i,
        type: settings.logs[i].y ? "log" : "value",
        min:
          axisMinMax[i].yMin !== null
            ? Number(axisMinMax[i].yMin)
            : settings.logs[i].y
            ? 0.1
            : 0,
        max: axisMinMax[i].yMax !== null ? Number(axisMinMax[i].yMax) : null,
        name: formatChartTitleWithSubscript(axis.title),
        nameLocation: "middle",
        nameGap: 30,
        nameTextStyle: AXIS_NAME_TEXT_STYLE,
        axisLabel: { fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE },
        offset: 0,
        alignTicks: shared.alignTicks,
        position: position,
        splitLine: {
          show: showSplitLine || showMinorSplitLineY
        },
        minorSplitLine: {
          show: showMinorSplitLineY,
          lineStyle: { color: "#F2F2F2" }
        },
        minorTick: {
          splitNumber: DEFAULT_AXIS_SPLIT_NUMBER
        }
      };
    }),
    legend: {
      top: "bottom",
      data: legendData,
      formatter: toUnicodeSubscript,
      show: legend.type === "bottom" && legend.visible
    },
    tooltip: shared.tooltip,
    dataZoom: shared.defaultDataZoom
  };
};
